import { RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Input } from "./ui/input.js";
import { cn } from "../lib/utils.js";

/**
 * A number that may be inherited. An empty box means "inherit", and the
 * placeholder shows what is inherited — so the effective value is always on
 * screen, whether it is set here or somewhere above.
 *
 * Commits on blur and on Enter, never per keystroke: every consumer writes
 * straight through to a PATCH, and one request per digit typed is noise.
 */
export function InheritedNumberField({
  value,
  inherited,
  inheritedLabel = "dem globalen Wert",
  onChange,
  min = 0,
  className,
  "aria-label": ariaLabel,
}: {
  value: number | null;
  /** Effective value while `value` is null; null while it is still loading. */
  inherited: number | null;
  /** Where an empty value falls back to, named in the reset button's tooltip. */
  inheritedLabel?: string;
  onChange: (value: number | null) => void;
  min?: number;
  className?: string;
  "aria-label": string;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));

  // A PATCH elsewhere (or a rejected one rolling back) has to win over a stale draft.
  useEffect(() => setDraft(value === null ? "" : String(value)), [value]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (value !== null) onChange(null);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < min) {
      setDraft(value === null ? "" : String(value));
      return;
    }
    if (parsed !== value) onChange(parsed);
  }

  return (
    <span className={cn("flex items-center gap-1", className)}>
      <Input
        value={draft}
        inputMode="numeric"
        aria-label={ariaLabel}
        placeholder={inherited === null ? "…" : String(inherited)}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setDraft(value === null ? "" : String(value));
        }}
        className="w-24 tabular-nums"
      />
      {/* The reset slot is reserved whether or not there is anything to reset —
          otherwise every inherited row's input sits 28px right of an overridden
          one, and the column stops being a column. Where an empty field falls
          back to is said in words next to the label, not out here. */}
      <span className="flex h-7 w-7 shrink-0 items-center justify-center">
        {value !== null ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            title={`Auf ${inheritedLabel} zurücksetzen`}
            aria-label={`${ariaLabel} auf ${inheritedLabel} zurücksetzen`}
            className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </span>
    </span>
  );
}
